import type { JSX } from 'react';
import React, { useState } from 'react';
import { safeRender } from '@/utils/safeRender';
import { buildShowMap } from '@/utils/buildShowMap';
import { normalizeTripleKey } from '@/utils/normalizeTripleKey';
import type { TransitItemAspects } from '@/store/api/types';
import { formatDateTime } from '@/utils/formatDateTime';
import { getWeeklyDateRange } from '@/utils/getWeeklyDateRange';
import { formatVerticalList } from '@/utils/renderVerticalCell';

import { useGetWeeklyPlanetAspectsQuery } from '@/store/api/weeklyPlanetAspects.api';
import { useGetWeeklyPlanetaryTransitQuery } from '@/store/api/weeklyPlanetaryTransit.api';
import { useGetWeeklyTransitWithAspectQuery } from '@/store/api/weeklyTransitWithAspect.api';
import Loader from '@/components/Loader';

export default function HousesAspects({
  activeWeek = 'current',
  onLoadingChange,
}: {
  activeWeek?: 'current' | 'next';
  onLoadingChange?: (loading: boolean) => void;
}): JSX.Element {
  const [showTransitJson, setShowTransitJson] = useState(false);
  const [showAspectsJson, setShowAspectsJson] = useState(false);
  const [showWeeklyTransitJson, setShowWeeklyTransitJson] = useState(false);
  const { fromDate, toDate } = getWeeklyDateRange(activeWeek);

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // const { data, isLoading } = useGetWeeklyAspectTransitQuery();

  const SIGN_MAP: Record<string, string> = {
    Aries: 'Mesha / Aries',
    Taurus: 'Vrishabha / Taurus',
    Gemini: 'Mithuna / Gemini',
    Cancer: 'Karkataka / Cancer',
    Leo: 'Simha / Leo',
    Virgo: 'Kanya / Virgo',
    Libra: 'Tula / Libra',
    Scorpio: 'Vrishchika / Scorpio',
    Sagittarius: 'Dhanur / Sagittarius',
    Capricorn: 'Makara / Capricorn',
    Aquarius: 'Kumbha / Aquarius',
    Pisces: 'Meena / Pisces',
  };

  const normalizeSign = (sign: string) => SIGN_MAP[sign] ?? sign;

  // const transitAspectData: TransitItemAspects[] =
  //   data?.flatMap((planetItem) =>
  //     planetItem.weeklyTransit.map((transit) => ({
  //       sign: transit.sign,
  //       house: transit.house,
  //       planet: planetItem.planet,

  //       from: transit.from,
  //       to: transit.to,

  //       natalPlanets: [],
  //       NatalPlanets: [],

  //       aspect: transit.aspects?.aspect ?? [],
  //       aspectSigns: transit.aspects?.aspectSigns ?? [],
  //       houseAspect: transit.aspects?.houseAspect ?? [],
  //       planetAspects: [],
  //     }))
  //   ) ?? [];

  const { data: aspectsData, isLoading: aspectsLoading } = useGetWeeklyPlanetAspectsQuery({
    weekType: activeWeek,
  });
  const { data: transitData, isLoading: transitLoading } = useGetWeeklyPlanetaryTransitQuery({
    weekType: activeWeek,
  });

  const { data: weeklyTransitAspectData, isLoading: weeklyTransitAspectLoading } =
    useGetWeeklyTransitWithAspectQuery({ weekType: activeWeek });

  let visualPlanetIndex = 0;

  const formatOnlyDate = (date?: string) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <>
      <div className="border border-[#2DAAFD] rounded-xl p-6 font-lato">
        <div className="flex justify-center mb-4">
          <div className="font-semibold text-[#082261] font-20 sm:font-22 tracking-wide">
            Date : {formatOnlyDate(fromDate)} – {formatOnlyDate(toDate)}
          </div>
        </div>

        <h3 className="text-[32px] font-semibold font-spectral mt-2 mb-6 text-center text-[#082261] uppercase">
          Planetary Transit Position
        </h3>

        <button
          onClick={() => setShowTransitJson((p) => !p)}
          className="mx-auto mb-6 block px-6 py-2 rounded-lg bg-gradient-to-r from-[#082261] to-[#34065c] text-white"
        >
          Click to show json data
        </button>

        {showTransitJson && (
          <pre className="bg-[#F4FAFF] border border-[#2DAAFD] rounded-lg p-4 overflow-x-auto text-sm">
            {JSON.stringify(transitData, null, 2)}
          </pre>
        )}

        <div className="overflow-x-auto">
          {aspectsLoading || transitLoading || weeklyTransitAspectLoading ? (
            <Loader />
          ) : (
            <table className="min-w-full border text border-[#2DAAFD] border-collapse text-black whitespace-nowrap">
              <thead className="bg-[#001B6B] text-white font-spectral uppercase">
                <tr>
                  <th className="border px-4 py-2">Transiting Planet(s)</th>
                  <th className="border px-4 py-2">Sign</th>
                  <th className="border px-4 py-2">Degree</th>
                  <th className="border px-4 py-2">Nakshatra</th>
                  <th className="border px-4 py-2">Pada</th>
                  <th className="border px-4 py-2">Awastha</th>
                  <th className="border px-4 py-2">Retro</th>
                </tr>
              </thead>

              <tbody className="text-black font-lato">
                {(() => {
                  const rows = transitData?.transit ?? [];

                  const getRowKey = (row: (typeof rows)[number]): string =>
                    row.sign ?? '__NO_SIGN__';

                  const groupKeyList = rows.map(getRowKey);

                  const groupBackground: Record<string, string> = {};
                  let toggle = true;

                  for (let i = 0; i < rows.length; i++) {
                    const key = groupKeyList[i];
                    if (!key) continue;

                    if (!groupBackground[key]) {
                      groupBackground[key] = toggle
                        ? 'bg-white'
                        : 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]';
                      toggle = !toggle;
                    }
                  }

                  const showMap = buildShowMap(rows, getRowKey);

                  return rows.map((row, idx) => {
                    const key = groupKeyList[idx];
                    if (!key) return null;
                    const show = showMap[idx];
                    const rowBg = groupBackground[key];

                    let groupLen = 1;
                    let back = idx - 1;
                    while (back >= 0 && groupKeyList[back] === key) {
                      groupLen++;
                      back--;
                    }
                    let fwd = idx + 1;
                    while (fwd < rows.length && groupKeyList[fwd] === key) {
                      groupLen++;
                      fwd++;
                    }

                    const nextKey = idx < rows.length - 1 ? groupKeyList[idx + 1] : null;

                    const isLastInGroup = nextKey !== key;

                    const mergedClass =
                      !show && groupLen >= 2
                        ? 'border-0 border-x border-black text-transparent select-none'
                        : '';

                    const forcedBorder =
                      groupLen >= 2 ? 'border-0 border-x border-black' : 'border border-black';

                    const finalShowClass = show ? forcedBorder : mergedClass;

                    const bottomBorderClass =
                      groupLen >= 2 && isLastInGroup ? 'border-b border-black' : '';

                    return (
                      <tr key={idx} className={rowBg}>
                        <td className="border border-black px-4 py-2 text-center">{row.planet}</td>

                        {show && (
                          <td
                            rowSpan={groupLen}
                            className={`px-4 py-2 text-center align-middle border border-black`}
                          >
                            {row.sign}
                          </td>
                        )}

                        <td className="border border-black px-4 py-2 text-center">{row.degree}°</td>

                        <td className="border border-black px-4 py-2 text-center">
                          {row.nakshatra}
                        </td>

                        <td className="border border-black px-4 py-2 text-center">{row.pada}</td>

                        <td className="border border-black px-4 py-2 text-center">{row.awastha}</td>

                        <td className="border border-black px-4 py-2 text-center">
                          {row.retro ? 'True' : 'False'}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          )}
        </div>

        <h3 className="text-[32px] font-semibold font-spectral mt-16 mb-6 text-center text-[#082261] uppercase">
          Aspects of Transiting Planets
        </h3>

        <button
          onClick={() => setShowAspectsJson((p) => !p)}
          className="mx-auto mb-6 block px-6 py-2 rounded-lg bg-gradient-to-r from-[#082261] to-[#34065c] text-white"
        >
          Click to show json data
        </button>

        {showAspectsJson && (
          <pre className="bg-[#F4FAFF] border border-[#2DAAFD] rounded-lg p-4 overflow-x-auto text-sm">
            {JSON.stringify(aspectsData, null, 2)}
          </pre>
        )}

        <div className="overflow-x-auto w-full">
          <table className="min-w-full border border-[#2DAAFD] border-collapse">
            <thead className="bg-[#001B6B] text-white font-spectral uppercase">
              <tr>
                <th className="border px-4 py-2">Transiting Planet(s)</th>
                <th className="border px-4 py-2">Aspects</th>
                <th className="border px-4 py-2">Signs Aspected</th>
              </tr>
            </thead>

            <tbody className="font-lato">
              {aspectsData?.map((row, idx) => (
                <tr
                  key={idx}
                  className={
                    idx % 2 === 0 ? 'bg-white' : 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]'
                  }
                >
                  <td className="border border-black px-4 py-2 text-center">{row.planet}</td>

                  <td className="border border-black px-4 py-2 text-center">
                    {row.aspect.map((a, i) => (
                      <div key={i}>{safeRender(a)}</div>
                    ))}
                  </td>

                  <td className="border border-black px-4 py-2 text-center">
                    {row.aspectSigns.map((s, i) => (
                      <div key={i}>{safeRender(s)}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-[32px] font-semibold font-spectral mt-16 mb-6 text-center text-[#082261] uppercase">
          Transition of Signs and Houses Aspected
        </h3>

        <button
          onClick={() => setShowWeeklyTransitJson((p) => !p)}
          className="mx-auto mb-6 block px-6 py-2 rounded-lg bg-gradient-to-r from-[#082261] to-[#34065c] text-white"
        >
          Click to show json data
        </button>

        {showWeeklyTransitJson && (
          <pre className="bg-[#F4FAFF] border border-[#2DAAFD] rounded-lg p-4 overflow-x-auto text-sm">
            {JSON.stringify(weeklyTransitAspectData, null, 2)}
          </pre>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full mt-4 border-collapse border border-[#2DAAFD] text-[16px] whitespace-nowrap">
            <thead className="bg-[#001B6B] text-white font-spectral uppercase">
              <tr>
                <th className="border px-8 py-2 ">Planet</th>
                <th className="border px-4 py-2 ">Sign Enter</th>
                <th className="border px-4 py-2">From</th>
                <th className="border px-4 py-2">To</th>
                {/* <th className="border px-4 py-2">House</th> */}
                <th className="border px-4 py-2">Aspect</th>
                <th className="border px-4 py-2">Aspected Sign</th>
                <th className="border px-4 py-2">Asta</th>
                <th className="border px-4 py-2">Vakri</th>
                {/* <th className="border px-4 py-2">House Aspect</th> */}
                <th className="border px-4 py-2">Details</th>
              </tr>
            </thead>

            <tbody className="text-black font-lato">
              {weeklyTransitAspectData?.map((planetItem, pIdx) => {
                const isEvenPlanet = visualPlanetIndex % 2 === 0;
                visualPlanetIndex++;

                const getHouseKey = (wt: any) => wt.signChangesAspect?.[0]?.house ?? '__NO_HOUSE__';

                return planetItem.weeklyTransit.map((wt, wIdx) => {
                  const rowKey = `${pIdx}-${wIdx}`;
                  const isExpanded = expandedRows[rowKey];
                  const hasSignChanges = wt.signChangesAspect.length > 0;

                  return (
                    <React.Fragment key={rowKey}>
                      {(hasSignChanges ? wt.signChangesAspect : [null]).map((s, idx) => (
                        <tr
                          className={
                            isEvenPlanet
                              ? 'bg-white'
                              : 'bg-[linear-gradient(to_right,#B7E2FF,#2DAAFD)]'
                          }
                          key={`${rowKey}-${idx}`}
                        >
                          {idx === 0 && (
                            <td
                              rowSpan={hasSignChanges ? wt.signChangesAspect.length : 1}
                              className="border border-black px-4 py-2 text-center align-middle"
                            >
                              {planetItem.planet}
                            </td>
                          )}

                          <td className="border border-black px-4 py-2 text-center">
                            {s ? s.sign : '-'}
                          </td>

                          <td className="border border-black px-4 py-2 text-center">
                            {s ? formatDateTime(s.from) : '-'}
                          </td>

                          <td className="border border-black px-4 py-2 text-center">
                            {s?.to && s.to !== '-' ? formatDateTime(s.to) : '-'}
                          </td>

                          {/* <td className="border border-black px-4 py-2 text-center">
                              {s ? s.house : '-'}
                            </td> */}

                          {idx === 0 && (
                            <td
                              rowSpan={hasSignChanges ? wt.signChangesAspect.length : 1}
                              className="border border-black px-4 py-2 text-center align-middle"
                            >
                              {s && s.aspects?.aspect?.length ? (
                                <div className="flex flex-col items-center leading-tight">
                                  {formatVerticalList(s.aspects.aspect).map((val, i) => (
                                    <span key={i}>{val}</span>
                                  ))}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          )}

                          <td className="border border-black px-4 py-2 text-center">
                            {s && s.aspects?.aspectSigns?.length ? (
                              <div className="flex flex-col items-center leading-tight">
                                {formatVerticalList(s.aspects.aspectSigns).map((val, i) => (
                                  <span key={i}>{val}</span>
                                ))}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>

                          {idx === 0 && (
                            <td
                              rowSpan={hasSignChanges ? wt.signChangesAspect.length : 1}
                              className="border border-black px-4 py-2 text-center align-middle"
                            >
                              {Array.isArray(wt?.asta) && wt.asta.length > 0 ? 'Yes' : 'No'}
                            </td>
                          )}

                          {idx === 0 && (
                            <td
                              rowSpan={hasSignChanges ? wt.signChangesAspect.length : 1}
                              className="border border-black px-4 py-2 text-center align-middle"
                            >
                              {Array.isArray(wt?.vakri) && wt.vakri.length > 0 ? 'Yes' : 'No'}
                            </td>
                          )}

                          {/* <td className="border border-black px-4 py-2 text-center">
                              {s ? s.aspects.houseAspect.join(', ') : '-'}
                            </td> */}

                          {idx === 0 && (
                            <td
                              rowSpan={hasSignChanges ? wt.signChangesAspect.length : 1}
                              className="border border-black px-4 py-2 text-center align-middle"
                            >
                              <button
                                onClick={() => toggleRow(rowKey)}
                                className="px-4 py-1 text-sm rounded-full
                              bg-gradient-to-r from-[#082261] to-[#34065c]
                              text-white
                              transition-all duration-300 ease-in-out
                              hover:scale-105 hover:shadow-lg"
                              >
                                {isExpanded ? 'Collapse' : 'Expand'}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}

                      {isExpanded && (
                        <tr className="bg-[#F4FAFF] transition-all duration-300 ease-in-out">
                          <td colSpan={9} className="border border-black px-6 py-4">
                            <div className="space-y-6 text-sm">
                              {wt.nakshtra?.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-[#082261] mb-2">Nakshatra</h4>
                                  {wt.nakshtra.map((n, i) => (
                                    <div key={i}>
                                      {n.nakshatra} – {formatDateTime(n.timestamp)}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {wt.pada?.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-[#082261] mb-2">Pada</h4>
                                  {wt.pada.map((p, i) => (
                                    <div key={i}>
                                      {p.nakshatra} – {p.pada} – {formatDateTime(p.timestamp)}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {wt.asta?.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-[#082261] mb-2">Asta</h4>
                                  {wt.asta.map((a, i) => (
                                    <div key={i}>
                                      {formatDateTime(a.startTime)} → {formatDateTime(a.endTime)} (
                                      {a.totalDuration})
                                    </div>
                                  ))}
                                </div>
                              )}

                              {wt.vakri?.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-[#082261] mb-2">Vakri</h4>
                                  {wt.vakri.map((v, i) => (
                                    <div key={i}>
                                      {formatDateTime(v.retrogradeTime)} →{' '}
                                      {formatDateTime(v.progressiveTime)} ({v.totalRetrograde})
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
